import { module as modujs } from "modujs";

const FOCUS_SELECTOR = 'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

export default class extends modujs {
	constructor(m) {
		super(m);
		this._openHandlers = new Map();
		this.events = { click: { close: "closeEvent" } };
	}

	init() {
		this.id = this.el.getAttribute("data-module-modal");

		this.triggers = Array.from(document.querySelectorAll(`[data-modal-open="${this.id}"]`));
		this.triggers.forEach((btn) => {
			const handler = this.open.bind(this, btn);
			this._openHandlers.set(btn, handler);
			btn.addEventListener("click", handler);
			btn.setAttribute("aria-expanded", "false");
		});

		// global keydown (Escape + Tab handling)
		this._onKeydown = this.onKeydown.bind(this);
		document.addEventListener("keydown", this._onKeydown);

		// guard focus from escaping the modal (covers Safari/edge cases)
		this._onFocusIn = (e) => {
			if (this.el.hidden) return;
			if (!this.el.contains(e.target)) {
				this._cacheFocusables();
				this.firstFocusable.focus({ preventScroll: true });
			}
		};

		document.addEventListener("focusin", this._onFocusIn);

		// optional overlay hooks
		this._closeFromOverlay = this.close.bind(this);
		document.addEventListener("overlay:click", this._closeFromOverlay);

		this.el.hidden = true;
		this.el.setAttribute("aria-hidden", "true");
		this.el.classList.remove("is-active");

		// background (ex: <main>) made inert while open
		this._main = document.querySelector("main") || document.body;
	}

	open(openerBtn) {
		this._lastFocused = openerBtn || document.activeElement;

		if (openerBtn) openerBtn.setAttribute("aria-expanded", "true");

		this.el.hidden = false;
		this.el.removeAttribute("aria-hidden");
		this.el.classList.add("is-active");

		this.el.setAttribute("aria-modal", "true");
		if (!this.el.hasAttribute("tabindex")) this.el.setAttribute("tabindex", "-1");

		// inert background
		// When we say “inert background” in the context of modals, it refers to the idea that everything outside the modal should be made unfocusable and ignored by assistive technologies while the modal is open.
		// When an element (like <main>) has inert:
		// All of its focusable elements are removed from the tab order.
		// Click/keyboard events don’t reach them.
		//Assistive tech treats them as if they are hidden.

		// So the only thing interactive is your modal.

		this._main.setAttribute("inert", "");

		// prepare focusables, then focus first or container
		this._cacheFocusables();
		this.firstFocusable.focus({ preventScroll: true });

		this.call?.("open", { aboveHeader: true }, "overlay");

		//event added in scroll.js
		document.dispatchEvent(new CustomEvent("lenisToggle", { detail: { status: "stopped" } }));
	}

	close() {
		this.el.classList.remove("is-active");
		this.el.setAttribute("aria-hidden", "true");
		this.el.hidden = true;

		// release background
		this._main.removeAttribute("inert");

		// return focus to the opener
		if (this._lastFocused && this._lastFocused.focus) {
			this._lastFocused.setAttribute("aria-expanded", "false");
			this._lastFocused.focus({ preventScroll: true });
		}

		this.call?.("close", {}, "overlay");

		//event added in scroll.js
		document.dispatchEvent(new CustomEvent("lenisToggle", { detail: { status: "started" } }));
	}

	// ---------- Keyboard / Focus trap ----------
	onKeydown(e) {
		if (this.el.hidden) return;

		if (e.key === "Escape") {
			e.preventDefault();
			this.close();
			return;
		}

		if (e.key === "Tab") {
			this._cacheFocusables();
			const active = document.activeElement;
			// pull focus inside if it somehow escaped
			if (!this.el.contains(active)) {
				e.preventDefault();
				this.firstFocusable.focus({ preventScroll: true });
				return;
			}

			const backward = e.shiftKey;

			if (!backward && active === this.lastFocusable) {
				e.preventDefault();
				this.firstFocusable.focus({ preventScroll: true });
			} else if (backward && active === this.firstFocusable) {
				e.preventDefault();
				this.lastFocusable.focus({ preventScroll: true });
			}
		}
	}

	_cacheFocusables() {
		const nodes = Array.from(this.el.querySelectorAll(FOCUS_SELECTOR));

		if (nodes.length === 0) {
			// fallback: loop on the container itself
			this.firstFocusable = this.el;
			this.lastFocusable = this.el;
			return;
		}

		this.firstFocusable = nodes[0];
		this.lastFocusable = nodes[nodes.length - 1];
	}

	closeEvent() {
		this.close();
	}

	destroy() {
		this._openHandlers.forEach((handler, btn) => btn.removeEventListener("click", handler));
		this._openHandlers.clear();

		document.removeEventListener("keydown", this._onKeydown);
		document.removeEventListener("focusin", this._onFocusIn);
		document.removeEventListener("overlay:click", this._closeFromOverlay);

		this._main?.removeAttribute("inert");
	}
}
